import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  Checkbox,
  Stack,
  Typography,
} from '@mui/material';

import { api_portalOfficeHospitalListing_medicalSubject } from '@/init/swagger';
import { useLogic } from './logic';
import { HospitalListingOutput } from '@/helpers/parseHospitalListings';
import { RequestStatusChip } from '../../HospitalSearchResults/RequestStatusChip';
import { PhoneIcon } from '@/features/icon/PhoneIcon';
import { MedicalCareIcon } from '@/features/icon/MedicalCareIcon';
import { HpUrlIcon } from '@/features/icon/HpUrlIcon';
import { CanRequestLabel } from '@/features/label/CanRequestLabel';
import { MuiChip } from '@/components/chip/MuiChip';

type Props = {
  hospitalMedicalSubjects: api_portalOfficeHospitalListing_medicalSubject[];
  listingInfo: HospitalListingOutput;
  checked: boolean;
  disabled: boolean;
  onSelect: (listingInfo: HospitalListingOutput) => void;
  mapMarkerPortalOfficeId?: number | null;
  isShowMap?: boolean;
};

export function HospitalListingItemForMultiRequest({
  hospitalMedicalSubjects,
  listingInfo,
  checked,
  disabled,
  onSelect,
  mapMarkerPortalOfficeId,
  isShowMap: _isShowMap,
}: Props) {
  const { thumbnailInfo, portalOfficeInfo, isCarelogood } = useLogic({
    hospitalMedicalSubjects,
    listingInfo,
  });

  const listingId = listingInfo.portal_office_id;
  const isHovered = mapMarkerPortalOfficeId === listingId;

  return (
    <Card
      sx={{
        containerName: 'hospital-listing-item-for-multi-request',
        containerType: 'inline-size',
        display: 'flex',
        flexDirection: {
          md: 'row',
        },
        boxShadow: disabled
          ? 'none'
          : isHovered
            ? `0px 0px 8px 0px rgba(62, 201, 190, 0.4), 0px 4px 16px 0px rgba(62, 201, 190, 0.2)`
            : `0px 0px 2px 0px rgba(0, 0, 0, 0.10), 0px 4px 8px 0px rgba(0, 0, 0, 0.10)`,
        borderRadius: 2,
        padding: 2,
        gap: {
          xs: 2,
          md: 3,
        },
        backgroundColor: disabled ? '#F9F9F9' : 'white',
        position: 'relative',
        border: isHovered
          ? '2px solid #3ec9be'
          : disabled
            ? '1px solid #E0E0E0'
            : 'none',
        '&:hover': {
          '& .highlight p': {
            textDecoration: 'underline',
            textDecorationColor: '#3ec9be',
            color: '#3ec9be',
          },
          '& .MuiCardMedia-img': {
            border: 'solid 2px #3ec9be',
          },
        },
        '@container hospital-listing-item-for-multi-request (width < 600px)': {
          flexDirection: 'column',
          gap: 2,
        },
      }}
    >
      <RequestStatusChip requestStatus={listingInfo.request_status} />

      <Checkbox
        checked={checked}
        name={`checkbox-${listingInfo.id}`}
        disabled={disabled}
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          onSelect(listingInfo);
        }}
        sx={{
          px: 1,
          '&:hover': {
            backgroundColor: '#3ec9be21',
            borderRadius: 'inherit',
          },
          '& .MuiTouchRipple-root': {
            display: 'none',
          },
        }}
      />

      <CardActionArea
        disabled={disabled}
        sx={{
          display: 'flex',
          flexDirection: {
            xs: 'column',
            md: 'row',
          },
          gap: {
            xs: 2,
            md: 3,
          },
          py: 1,
          px: {
            xs: 0.5,
            md: 2,
          },
          alignItems: 'flex-start',
          width: '100%',
          '&:hover': {
            '& .highlight p': {
              textDecoration: 'underline',
              textDecorationColor: '#3ec9be',
              color: '#3ec9be',
            },
            '& .MuiCardMedia-img': {
              border: 'solid 2px #3ec9be',
            },
          },
          '@container hospital-listing-item-for-multi-request (width < 600px)':
            {
              flexDirection: 'column',
              gap: 2,
              px: 0.5,
            },
        }}
        onClick={() => {
          window.open(
            `/app/hospitals/${listingInfo.portal_office_id}`,
            '_blank',
            'noopener,noreferrer',
          );
        }}
      >
        <Box
          sx={{
            width: {
              xs: '100%',
              md: 320,
            },
            maxWidth: {
              xs: '100%',
              md: '40%',
            },
            aspectRatio: '16 / 9',
            height: {
              xs: 263,
              md: 220,
            },
            borderRadius: 1,
            margin: 'auto',
            position: 'relative',
            overflow: 'hidden',
            '@container hospital-listing-item-for-multi-request (width < 600px)':
              {
                aspectRatio: '1 / 1',
                width: '100%',
                maxWidth: '100%',
                height: 'auto',
                maxHeight: 263,
              },
          }}
        >
          <img
            src={thumbnailInfo.image}
            alt={thumbnailInfo.alt}
            style={{
              borderRadius: 'inherit',
              objectFit: 'cover',
              width: '100%',
              height: '100%',
            }}
          />
        </Box>
        <CardContent
          sx={{
            display: 'flex',
            flexDirection: 'column',
            p: 0,
            py: 1,
            gap: 3,
            ':last-child': { pb: 1 },
            flex: 1,
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  justifyContent: 'flex-start',
                  gap: 1,
                }}
              >
                <Box>
                  <MuiChip
                    label={`医科`}
                    size="small"
                    sx={{
                      fontWeight: 700,
                      backgroundColor: '#FFAC8A',
                      height: '24px',
                      fontSize: '14px',
                    }}
                  />
                </Box>

                <div className="highlight">
                  <Typography
                    sx={{
                      verticalAlign: 'sub',
                      fontSize: '20px',
                      fontWeight: 700,
                    }}
                  >
                    {portalOfficeInfo.name}
                  </Typography>
                </div>
              </Box>
            </Box>

            {isCarelogood && (
              <Stack
                direction="row"
                justifyContent="flex-start"
                alignItems="center"
                gap={1.5}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                    gap: 1,
                  }}
                >
                  <CanRequestLabel />
                </Box>
              </Stack>
            )}

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <Typography
                color="#595960"
                variant="JP/Body/m/Regular/Normal"
                sx={{
                  order: {
                    xs: 1,
                    md: 2,
                  },
                }}
              >
                {portalOfficeInfo.postCode
                  ? `${portalOfficeInfo.postCode} ${portalOfficeInfo.address}`
                  : portalOfficeInfo.address}
              </Typography>
            </Box>

            {portalOfficeInfo.tel && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                <PhoneIcon color="default" width={16} height={16} />
                <Typography
                  color="#595960"
                  variant="JP/Body/m/Regular/Normal"
                  sx={{
                    order: {
                      xs: 1,
                      md: 2,
                    },
                  }}
                >
                  {portalOfficeInfo.tel}
                </Typography>
              </Box>
            )}

            {portalOfficeInfo.medicalSubjects && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                <MedicalCareIcon color="default" width={18} height={18} />
                <Typography
                  color="#595960"
                  variant="JP/Body/m/Regular/Normal"
                  sx={{
                    order: {
                      xs: 1,
                      md: 2,
                    },
                  }}
                >
                  {portalOfficeInfo.medicalSubjects}
                </Typography>
              </Box>
            )}

            {portalOfficeInfo.hpUrl && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                <HpUrlIcon
                  color="default"
                  width={18}
                  height={18}
                  sxProps={{
                    marginTop: '2px',
                  }}
                />
                <Typography
                  color="#595960"
                  variant="JP/Body/m/Regular/Normal"
                  sx={{
                    order: {
                      xs: 1,
                      md: 2,
                    },
                  }}
                >
                  {portalOfficeInfo.hpUrl}
                </Typography>
              </Box>
            )}

            <Box
              sx={{
                marginTop: 1,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <Typography
                variant="JP/Body/s/Medium/Tight"
                sx={{
                  fontSize: '14px',
                }}
                color="#C4C4C4"
              >
                更新日
              </Typography>
              <Typography
                variant="JP/Body/s/Medium/Tight"
                sx={{
                  fontSize: '14px',
                }}
                color="#C4C4C4"
              >
                {portalOfficeInfo.updatedAt}
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </CardActionArea>
    </Card>
  );
}
